import jsPDF from "jspdf";
import {
  formatDate,
  formatDuration,
  getUserData,
  getUserDataValue,
} from "common/utilities";

type ProcessExportRecord = Record<string, any>;

export type ProcessExportFormat = "csv" | "pdf" | "print";
export type ProcessExportColumnType = "standard" | "userdata";

export interface ProcessExportColumn {
  id: string;
  label: string;
  type: ProcessExportColumnType;
  userDataKey?: string;
}

export const PROCESS_EXPORT_STANDARD_COLUMNS: ProcessExportColumn[] = [
  { id: "idprocess", label: "ID", type: "standard" },
  { id: "processName", label: "Process name", type: "standard" },
  { id: "process_description_detail", label: "Description", type: "standard" },
  { id: "process", label: "Model name", type: "standard" },
  { id: "task", label: "Task", type: "standard" },
  { id: "status", label: "Status", type: "standard" },
  { id: "startDate", label: "Start date", type: "standard" },
  { id: "endDate", label: "End date", type: "standard" },
  { id: "processDuration", label: "Process duration", type: "standard" },
  { id: "waitingTime", label: "Waiting time", type: "standard" },
  { id: "workingTime", label: "Working time", type: "standard" },
  { id: "priority", label: "Priority", type: "standard" },
  { id: "labels", label: "Labels", type: "standard" },
  { id: "groups", label: "Groups", type: "standard" },
  { id: "roles", label: "Roles", type: "standard" },
  { id: "participants", label: "Participants", type: "standard" },
];

const HISTORY_HIDDEN_COLUMNS = new Set(["task", "status", "waitingTime", "workingTime"]);
const LIVE_HIDDEN_COLUMNS = new Set(["endDate"]);

export const getProcessExportStandardColumns = (history: boolean) =>
  PROCESS_EXPORT_STANDARD_COLUMNS.filter((column) =>
    history ? !HISTORY_HIDDEN_COLUMNS.has(column.id) : !LIVE_HIDDEN_COLUMNS.has(column.id)
  );

export const getProcessExportUserDataKeys = (processes: ProcessExportRecord[]) => {
  const keys = new Set<string>();
  processes.forEach((process) => {
    Object.keys(getUserData(process.process_userdata)).forEach((key) => keys.add(key));
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
};

export const getProcessExportUserDataColumns = (keys: string[]): ProcessExportColumn[] =>
  keys.map((key) => ({
    id: `userdata:${key}`,
    label: key,
    type: "userdata",
    userDataKey: key,
  }));

const formatExportValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.map(formatExportValue).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const parseArrayField = (value: unknown): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getArrayNames = (value: unknown, keys: string[]) =>
  parseArrayField(value)
    .map((item) => {
      if (!item || typeof item !== "object") return "";
      const foundKey = keys.find((key) => item[key]);
      return foundKey ? item[foundKey] : "";
    })
    .filter(Boolean)
    .join(", ");

const getStatusLabel = (state: number | undefined) => {
  if (state === 0) return "To do";
  if (state === 1) return "Doing";
  if (state === 2) return "Done";
  return "";
};

const getStandardFieldValue = (process: ProcessExportRecord, columnId: string) => {
  switch (columnId) {
    case "idprocess":
      return process.idprocess;
    case "process":
      return process.processmodelname;
    case "processName":
      return process.process_description || process.description;
    case "process_description_detail":
      return process.process_description_detail;
    case "task":
      return process.flownodename;
    case "status":
      return getStatusLabel(process.state);
    case "startDate":
      return formatDate(process.process_startdate);
    case "endDate":
      return formatDate(process.process_enddate);
    case "processDuration":
      return process.processduration ? formatDuration(process.processduration) : "";
    case "waitingTime":
      return process.timetotakeover ? formatDuration(process.timetotakeover) : "";
    case "workingTime":
      return process.timeindoing ? formatDuration(process.timeindoing) : "";
    case "priority":
      return process.priorityname;
    case "labels":
      return getArrayNames(process.jsontag, ["tag", "name"]);
    case "groups":
      return getArrayNames(process.jsongroups, ["groupname", "name"]);
    case "roles":
      return getArrayNames(process.jsonroles, ["rolename", "name"]);
    case "participants":
      return process.userfullname;
    default:
      return "";
  }
};

export const buildProcessExportRows = (
  processes: ProcessExportRecord[],
  columns: ProcessExportColumn[]
) =>
  processes.map((process) => {
    const userData = getUserData(process.process_userdata);

    return columns.reduce<Record<string, string>>((row, column) => {
      if (column.type === "userdata" && column.userDataKey) {
        row[column.label] = formatExportValue(
          userData[column.userDataKey] ? getUserDataValue(userData[column.userDataKey]) : ""
        );
        return row;
      }

      row[column.label] = formatExportValue(getStandardFieldValue(process, column.id));
      return row;
    }, {});
  });

const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

export const buildProcessExportCsv = (
  rows: Record<string, string>[],
  columns: ProcessExportColumn[]
) => {
  const headers = columns.map((column) => column.label);
  const body = rows.map((row) => headers.map((header) => escapeCsvValue(row[header] || "")).join(";"));
  return [headers.map(escapeCsvValue).join(";"), ...body].join("\r\n");
};

const getExportFileName = (title: string, extension: string) => {
  const date = new Date().toISOString().slice(0, 10);
  const normalizedTitle = title.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  return `${normalizedTitle || "processes"}-${date}.${extension}`;
};

export const downloadProcessExportCsv = (
  rows: Record<string, string>[],
  columns: ProcessExportColumn[],
  title: string
) => {
  const csv = buildProcessExportCsv(rows, columns);
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = getExportFileName(title, "csv");
  anchor.click();
  URL.revokeObjectURL(url);
};

export const downloadProcessExportPdf = (
  rows: Record<string, string>[],
  columns: ProcessExportColumn[],
  title: string
) => {
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const headers = columns.map((column) => column.label);
  const columnWidth = Math.max(28, (pageWidth - margin * 2) / Math.max(headers.length, 1));
  let y = 18;

  doc.setFontSize(14);
  doc.text(title, margin, y);
  y += 10;
  doc.setFontSize(8);

  const drawHeader = () => {
    doc.setFont("helvetica", "bold");
    headers.forEach((header, index) => {
      doc.text(doc.splitTextToSize(header, columnWidth - 2), margin + index * columnWidth, y);
    });
    doc.setFont("helvetica", "normal");
    y += 8;
  };

  drawHeader();
  rows.forEach((row) => {
    const cells = headers.map((header) => doc.splitTextToSize(row[header] || "", columnWidth - 2));
    const rowHeight = Math.max(...cells.map((cell) => cell.length), 1) * 4 + 2;
    if (y + rowHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
      drawHeader();
    }
    cells.forEach((cell, index) => {
      doc.text(cell, margin + index * columnWidth, y);
    });
    y += rowHeight;
  });

  doc.save(getExportFileName(title, "pdf"));
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const printProcessExport = (
  rows: Record<string, string>[],
  columns: ProcessExportColumn[],
  title: string
) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const printDocument = iframe.contentWindow?.document;
  if (!printDocument || !iframe.contentWindow) {
    document.body.removeChild(iframe);
    return false;
  }

  const headers = columns.map((column) => column.label);
  const headerCells = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("");
  const bodyRows = rows
    .map((row) => `<tr>${headers.map((header) => `<td>${escapeHtml(row[header] || "")}</td>`).join("")}</tr>`)
    .join("");

  printDocument.open();
  printDocument.write(`
    <!doctype html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #1f2933; }
          h1 { font-size: 20px; margin: 0 0 16px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th, td { border: 1px solid #d7dde3; padding: 6px; text-align: left; vertical-align: top; }
          th { background: #eef4f5; font-weight: 700; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
    </html>
  `);
  printDocument.close();

  let printed = false;
  const runPrint = () => {
    if (printed) return;
    printed = true;
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  };

  iframe.onload = runPrint;
  setTimeout(runPrint, 0);

  return true;
};
